<template>
  <div class="layout-container">
    <div class="sidebar-container" :class="{ 'is-collapse': !sidebar.opened }">
      <div class="sidebar-logo">
        <img :src="logoUrl" class="logo-img" v-if="sidebar.opened" />
        <span class="logo-title" v-if="sidebar.opened">{{ systemName }}</span>
        <span class="logo-mini" v-else>{{ shortSystemName }}</span>
      </div>
      <el-scrollbar wrap-class="scrollbar-wrapper">
        <el-menu
          :default-active="activeMenu"
          :collapse="!sidebar.opened"
          :unique-opened="true"
          :collapse-transition="false"
          mode="vertical"
          background-color="transparent"
          text-color="#a79ba9"
          active-text-color="#e14d68"
        >
          <sidebar-item
            v-for="route in routes"
            :key="route.path"
            :item="route"
            :base-path="route.path"
          />
        </el-menu>
      </el-scrollbar>
    </div>
    <div class="main-container" :class="{ 'sidebar-hide': !sidebar.opened }">
      <div class="navbar">
        <div class="navbar-left">
          <div class="hamburger" @click="toggleSidebar">
            <i :class="sidebar.opened ? 'el-icon-s-fold' : 'el-icon-s-unfold'"></i>
          </div>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item v-for="item in levelList" :key="item.path">
              <span>{{ item.meta.title }}</span>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="navbar-right">
          <el-dropdown class="avatar-container" trigger="click">
            <div class="avatar-wrapper">
              <img :src="avatar" class="user-avatar" />
              <span class="user-name">{{ adminInfo ? adminInfo.nickname : '管理员' }}</span>
              <span class="user-role" v-if="roleLabel">{{ roleLabel }}</span>
              <i class="el-icon-caret-bottom"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="showProfile">
                <i class="el-icon-user"></i> 个人资料
              </el-dropdown-item>
              <el-dropdown-item @click.native="showPassword">
                <i class="el-icon-key"></i> 修改密码
              </el-dropdown-item>
              <el-dropdown-item divided @click.native="logout">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      <el-main class="app-main">
        <router-view :key="key" />
      </el-main>
    </div>

    <el-dialog title="个人资料" :visible.sync="profileVisible" width="500px">
      <el-form :model="profileForm" label-width="80px">
        <el-form-item label="用户名">
          <el-input v-model="profileForm.username" disabled></el-input>
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="profileForm.nickname"></el-input>
        </el-form-item>
        <el-form-item label="头像">
          <el-upload
            class="avatar-uploader"
            action="/api/admin/auth/upload/avatar"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleAvatarSuccess"
          >
            <img v-if="profileForm.avatar" :src="getAvatarUrl(profileForm.avatar)" class="avatar-preview" />
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="profileVisible = false">取消</el-button>
        <el-button type="primary" @click="saveProfile">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="修改密码" :visible.sync="passwordVisible" width="500px">
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" show-password></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" show-password></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="passwordVisible = false">取消</el-button>
        <el-button type="primary" @click="savePassword">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SidebarItem from './components/SidebarItem.vue'
import Cookies from 'js-cookie'
import { getProfile, updateProfile, updatePassword } from '@/api/auth'
import { getPublicConfigs } from '@/api/system'
import { filterRoutesByMenus } from '@/utils/menu'
import { setBeanRate } from '@/utils/bean'
import { resolveLogo, shortName, formatPageTitle } from '@/utils/system-info'

export default {
  name: 'Layout',
  components: { SidebarItem },
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      levelList: [],
      profileVisible: false,
      passwordVisible: false,
      profileForm: {
        username: '',
        nickname: '',
        avatar: ''
      },
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    // 本地缓存只用于"立刻有东西可渲染"，真实权限以服务端为准 ——
    // 角色菜单被超管改过之后，刷新页面就应该生效，不用等重新登录。
    this.refreshProfile()
    this.loadBeanRate()
    // 系统名 / logo 来自系统配置，跟小程序端同源；这里只在"还没拉过"时请求
    this.$store.dispatch('loadSystemInfo')
  },
  computed: {
    ...mapGetters(['sidebar', 'adminInfo', 'menus', 'roleLabel', 'systemInfo']),
    /** 侧边栏系统名（system.name），运营在「系统信息」里改完即时生效 */
    systemName() {
      return this.systemInfo.name
    },
    /** 折叠态只放一个字 */
    shortSystemName() {
      return shortName(this.systemInfo.name)
    },
    logoUrl() {
      return resolveLogo(this.systemInfo.logo)
    },
    routes() {
      return filterRoutesByMenus(this.$router.options.routes, this.menus)
    },
    activeMenu() {
      const route = this.$route
      const { meta, path } = route
      if (meta.activeMenu) {
        return meta.activeMenu
      }
      return path
    },
    key() {
      return this.$route.path
    },
    avatar() {
      const avatar = this.adminInfo && this.adminInfo.avatar
      return this.getAvatarUrl(avatar)
    },
    uploadHeaders() {
      return {
        Authorization: 'Bearer ' + Cookies.get('admin_token')
      }
    }
  },
  watch: {
    $route: {
      handler(route) {
        this.getBreadcrumb()
      },
      immediate: true
    },
    // 系统名是异步取回来的，取到后再把标签页标题改成配置里的名字
    systemName: {
      handler() {
        this.applyPageTitle()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['toggleSidebar', 'setAdminInfo']),
    applyPageTitle() {
      // 与路由守卫同一套格式，避免切页面时标题又变回写死的旧名字
      const route = this.$route
      document.title = formatPageTitle(route.meta && route.meta.title, this.systemName)
    },
    refreshProfile() {
      getProfile().then(res => {
        if (!res || !res.data) {
          return
        }
        this.setAdminInfo(res.data)

        // 权限刚刚收紧（比如超管收回了某个菜单），当前页面可能已经不该看了
        const menus = res.data.menus || []
        const current = this.$route.path
        if (menus.length && current !== '/' && !menus.includes(current)) {
          this.$router.replace(menus[0])
        }
      }).catch(() => {})
    },
    loadBeanRate() {
      // 爱豆换算率是公开配置，管理端也要用它把「元」显示成「爱豆」
      getPublicConfigs().then(res => {
        const list = (res && res.data) || []
        const rate = list.find(item => item.configKey === 'bean.per_yuan')
        if (rate) {
          setBeanRate(rate.configValue)
        }
      }).catch(() => {})
    },
    getAvatarUrl(avatar) {
      if (!avatar) return 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
        return avatar
      }
      return avatar
    },
    getBreadcrumb() {
      let matched = this.$route.matched.filter(item => item.meta && item.meta.title)
      const first = matched[0]
      if (!this.isDashboard(first)) {
        matched = [{ path: '/dashboard', meta: { title: '首页' } }].concat(matched)
      }
      this.levelList = matched
    },
    isDashboard(route) {
      const name = route && route.name
      if (!name) {
        return false
      }
      return name.trim().toLowerCase() === 'Dashboard'.toLowerCase()
    },
    showProfile() {
      getProfile().then(res => {
        this.profileForm = {
          username: res.data.username,
          nickname: res.data.nickname,
          avatar: res.data.avatar
        }
        this.profileVisible = true
      })
    },
    handleAvatarSuccess(res) {
      if (res.code === 200) {
        this.profileForm.avatar = res.data.url
      }
    },
    saveProfile() {
      // ⚠️ 只提交后端 DTO 认识的字段。profileForm 里还带着 username，
      // 而 AdminUpdateProfileDTO 只有 nickname / avatar —— 多传未知字段会
      // 被 Jackson 直接判 400（表现就是「请求参数错误」）；用户名本来也不允许改。
      const payload = {
        nickname: this.profileForm.nickname,
        avatar: this.profileForm.avatar
      }
      updateProfile(payload).then(() => {
        this.$message.success('保存成功')
        this.profileVisible = false
        // 只回写这两个字段：role / menus / schoolIds 都是服务端下发的，别被表单带着走
        this.setAdminInfo({ ...this.adminInfo, nickname: payload.nickname, avatar: payload.avatar })
      })
    },
    showPassword() {
      this.passwordForm = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordVisible = true
    },
    savePassword() {
      this.$refs.passwordForm.validate(valid => {
        if (valid) {
          updatePassword(this.passwordForm).then(() => {
            this.$message.success('密码修改成功')
            this.passwordVisible = false
          })
        }
      })
    },
    logout() {
      this.$confirm('确定要退出登录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$store.dispatch('logout').then(() => {
          this.$router.push('/login')
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.layout-container {
  display: flex;
  height: 100vh;
  width: 100%;
}

.sidebar-container {
  width: 210px;
  height: 100%;
  background-color: var(--ink-900);
  overflow: hidden;
  transition: width 0.3s;
  position: relative;
  z-index: 1001;

  &.is-collapse {
    width: 54px;
  }

  .sidebar-logo {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 12px;
    background-color: var(--ink-800);
    overflow: hidden;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    .logo-img {
      flex: none;
      width: 30px;
      height: 30px;
      border-radius: 7px;
      object-fit: cover;
    }

    .logo-title {
      color: #fff;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 0.5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .logo-mini {
      color: var(--rose-400);
      font-size: 20px;
      font-weight: 700;
    }
  }

  /* 菜单：深墨底 + 玫瑰激活条 */
  ::v-deep .el-menu {
    background-color: transparent;
    border-right: none;
  }

  ::v-deep .el-menu-item,
  ::v-deep .el-submenu__title {
    height: 44px;
    line-height: 44px;
    font-size: 13.5px;
    border-left: 3px solid transparent;

    i {
      color: inherit;
    }

    &:hover {
      background-color: rgba(255, 255, 255, 0.05) !important;
      color: #fff !important;
    }
  }

  ::v-deep .el-menu-item.is-active {
    background-color: rgba(225, 77, 104, 0.14) !important;
    border-left-color: var(--rose-400);
    font-weight: 500;
  }

  ::v-deep .el-submenu.is-active > .el-submenu__title {
    color: #fff !important;
  }

  ::v-deep .el-submenu .el-menu-item {
    background-color: rgba(0, 0, 0, 0.18) !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.05) !important;
    }
  }
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: margin-left 0.3s;
  margin-left: 10px;
  background-color: var(--paper);

  &.sidebar-hide {
    margin-left: 10px;
  }
}

.navbar {
  height: 52px;
  background-color: #fff;
  border-bottom: 1px solid var(--ink-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  position: relative;
  z-index: 9;

  .navbar-left {
    display: flex;
    align-items: center;

    .hamburger {
      cursor: pointer;
      font-size: 19px;
      padding: 0 10px;
      color: var(--ink-500);
      border-radius: var(--r-md);
      transition: all 0.16s;

      &:hover {
        background: var(--rose-50);
        color: var(--rose-600);
      }
    }
  }

  .navbar-right {
    .avatar-container {
      cursor: pointer;

      .avatar-wrapper {
        display: flex;
        align-items: center;
        padding: 4px 8px;
        border-radius: var(--r-md);
        transition: background 0.16s;

        &:hover {
          background: var(--ink-100);
        }

        .user-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: var(--ink-100);
          object-fit: cover;
        }

        .user-name {
          margin: 0 8px 0 8px;
          font-size: 14px;
          color: var(--ink-900);
        }

        .user-role {
          font-size: 12px;
          color: var(--rose-600);
          background: var(--rose-50);
          border-radius: 10px;
          padding: 2px 8px;
          margin-right: 6px;
        }

        i {
          color: var(--ink-350);
          font-size: 12px;
        }
      }
    }
  }
}

.app-main {
  padding: 20px 20px 28px;
  overflow: auto;
  background-color: var(--paper);
}

.avatar-uploader {
  ::v-deep .el-upload {
    border: 1px dashed var(--ink-300);
    border-radius: var(--r-md);
    cursor: pointer;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: var(--rose-500);
    }
  }

  .avatar-uploader-icon {
    font-size: 28px;
    color: var(--ink-350);
    width: 100px;
    height: 100px;
    line-height: 100px;
    text-align: center;
  }

  .avatar-preview {
    width: 100px;
    height: 100px;
    display: block;
    object-fit: cover;
  }
}
</style>
